import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Search,
  Filter,
  RefreshCw,
  Calendar as CalendarIcon,
  Download,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { DateRange } from "react-day-picker";

// Mock Data Interface
export interface LogEntry {
  id: string;
  timestamp: string;
  user: {
    name: string;
    email: string;
    avatar?: string;
  };
  action:
    | "login"
    | "create_account"
    | "update_role"
    | "delete_item"
    | "export_data"
    | "system_update";
  details: string;
  ipAddress: string;
  status: "success" | "failed" | "warning";
}

// Mock Data Generator
const generateMockLogs = (count: number): LogEntry[] => {
  const actions: LogEntry["action"][] = [
    "login",
    "create_account",
    "update_role",
    "delete_item",
    "export_data",
    "system_update",
  ];
  const statuses: LogEntry["status"][] = [
    "success",
    "success",
    "success",
    "warning",
    "failed",
  ];
  const users = [
    { name: "Nguyễn Văn A", email: "nguyenvana@example.com" },
    { name: "Trần Thị B", email: "tranthib@example.com" },
    { name: "Lê Văn C", email: "levanc@example.com" },
    { name: "System Admin", email: "admin@system.com" },
  ];

  return Array.from({ length: count }).map((_, i) => {
    const user = users[Math.floor(Math.random() * users.length)];
    const action = actions[Math.floor(Math.random() * actions.length)];
    const date = new Date();
    date.setMinutes(date.getMinutes() - i * 15); // Stagger times

    return {
      id: `log-${i}`,
      timestamp: date.toISOString(),
      user,
      action,
      details: getActionDetails(action),
      ipAddress: `192.168.1.${Math.floor(Math.random() * 255)}`,
      status: statuses[Math.floor(Math.random() * statuses.length)],
    };
  });
};

const getActionDetails = (action: string) => {
  switch (action) {
    case "login":
      return "Đăng nhập thành công vào hệ thống";
    case "create_account":
      return "Tạo mới tài khoản người dùng nhân viên";
    case "update_role":
      return "Cập nhật quyền hạn cho nhóm Quản lý";
    case "delete_item":
      return "Xóa bản ghi #4291 khỏi cơ sở dữ liệu";
    case "export_data":
      return "Xuất báo cáo doanh thu tháng 10";
    case "system_update":
      return "Hệ thống tự động sao lưu dữ liệu";
    default:
      return "Thực hiện tác vụ hệ thống";
  }
};

const ActionBadge = ({ action }: { action: string }) => {
  const styles = {
    login: "bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200",
    create_account:
      "bg-green-100 text-green-700 hover:bg-green-100 border-green-200",
    update_role:
      "bg-purple-100 text-purple-700 hover:bg-purple-100 border-purple-200",
    delete_item: "bg-red-100 text-red-700 hover:bg-red-100 border-red-200",
    export_data: "bg-gray-100 text-gray-700 hover:bg-gray-100 border-gray-200",
    system_update:
      "bg-orange-100 text-orange-700 hover:bg-orange-100 border-orange-200",
  };

  const labels = {
    login: "Đăng nhập",
    create_account: "Tạo tài khoản",
    update_role: "Cập nhật vai trò",
    delete_item: "Xóa dữ liệu",
    export_data: "Xuất dữ liệu",
    system_update: "Hệ thống",
  };

  // @ts-ignore
  const style = styles[action] || "bg-gray-100 text-gray-800";
  // @ts-ignore
  const label = labels[action] || action;

  return (
    <Badge variant="outline" className={cn("font-medium border", style)}>
      {label}
    </Badge>
  );
};

export default function ActivityLog() {
  const [logs, setLogs] = useState<LogEntry[]>(generateMockLogs(15));
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Simulate network request
    setTimeout(() => {
      setLogs(generateMockLogs(15));
      setIsRefreshing(false);
    }, 800);
  };

  // Filter logs logic (mock frontend filtering)
  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      log.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase());

    // Add date filtering logic here if needed for mock
    return matchesSearch;
  });

  return (
    <div >
      {/* Header */}
      <div className="border-b-2 border-green-600 p-4 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Lịch sử hoạt động
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="bg-white" onClick={() => {}}>
            <Download className="mr-2 h-4 w-4" />
            Xuất CSV
          </Button>
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={cn(isRefreshing && "opacity-80")}
          >
            <RefreshCw
              className={cn("mr-2 h-4 w-4", isRefreshing && "animate-spin")}
            />
            {isRefreshing ? "Đang tải..." : "Làm mới"}
          </Button>
        </div>
      </div>

      <div className="p-6 flex flex-col gap-4"> 
        {/* Filter Bar */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-4 md:space-y-0 md:flex md:items-center md:justify-between gap-4">
          <div className="flex-1 flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Tìm kiếm người dùng, hành động..."
                className="pl-9 bg-slate-50 border-slate-200 focus:bg-white transition-colors"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Date Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-[260px] justify-start text-left font-normal bg-slate-50 border-slate-200 hover:bg-slate-100",
                    !dateRange && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "dd/MM/yyyy")} -{" "}
                        {format(dateRange.to, "dd/MM/yyyy")}
                      </>
                    ) : (
                      format(dateRange.from, "dd/MM/yyyy")
                    )
                  ) : (
                    <span>Chọn khoảng thời gian</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            {/* User Filter (Mock) */}
            <Select>
              <SelectTrigger className="w-[200px] bg-slate-50 border-slate-200">
                <SelectValue placeholder="Tất cả người dùng" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả người dùng</SelectItem>
                <SelectItem value="admin">System Admin</SelectItem>
                <SelectItem value="staff">Nhân viên</SelectItem>
                <SelectItem value="manager">Quản lý</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Filter Toggle (Visual only for now) */}
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:flex text-slate-500"
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>

        {/* Log Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50/50">
              <TableRow>
                <TableHead className="w-[200px]">Thời gian</TableHead>
                <TableHead className="w-[250px]">Người dùng</TableHead>
                <TableHead className="w-[180px]">Hành động</TableHead>
                <TableHead className="min-w-[300px]">Chi tiết</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.length > 0 ? (
                filteredLogs.map((log) => (
                  <TableRow
                    key={log.id}
                    className="hover:bg-slate-50/50 transition-colors"
                  >
                    <TableCell className="font-medium text-slate-600">
                      {format(new Date(log.timestamp), "HH:mm dd/MM/yyyy")}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-900">
                          {log.user.name}
                        </span>
                        <span className="text-xs text-slate-500">
                          {log.user.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ActionBadge action={log.action} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-700">{log.details}</span>
                        {log.status === "failed" && (
                          <Badge
                            variant="destructive"
                            className="h-5 px-1.5 text-[10px]"
                          >
                            Failed
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="h-24 text-center text-slate-500"
                  >
                    Không tìm thấy kết quả nào.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination Footer */}
          <div className="flex items-center justify-between px-4 py-4 border-t border-slate-100 bg-slate-50/30">
            <div className="text-sm text-slate-500">
              Hiển thị <strong>1-15</strong> trong tổng số <strong>450</strong>{" "}
              dòng
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 bg-white border-slate-300 text-slate-900"
              >
                1
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-slate-500"
              >
                2
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-slate-500"
              >
                3
              </Button>
              <span className="text-slate-400">...</span>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
